import {throttle} from 'lodash';
import {useCallback, useEffect, useRef, useState} from 'react';
import {useEscpacePress} from '@/hooks';
import {GeoDataItem} from '@/map/@types';
import {useQueryRegionByPoint} from '@/map/api';
import {MAP_QUERY_DEBOUNCE_SHORT_MS, SHAPE_COLOR_OPTIONS} from '@/map/constants';
import {AreaOverlayType, GeoDataType} from '@/map/enums';
import {palette} from '@/styles';
import {EmdArea} from '../Overlay';
import {DrawInfoCursor} from './DrawInfoCursor';

interface IProps {
  map: naver.maps.Map;
  colorOptions?: {
    fillColor?: string;
    fillOpacity?: number;
    strokeColor?: string;
    color?: string;
    strokeWeight?: number;
  };
  selectedEmds?: GeoDataItem[];
  displayNameTag?: boolean;
  onCancel?(): void;
  onClick?(emd: GeoDataItem): void;
}

const DEFAULT_COLOR_OPTIONS: IProps['colorOptions'] = {
  ...SHAPE_COLOR_OPTIONS[AreaOverlayType.EMD],
  color: palette.white,
  fillOpacity: 0.5,
  strokeWeight: 2,
};

export const EmdCursor = ({
  map,
  colorOptions = DEFAULT_COLOR_OPTIONS,
  displayNameTag,
  selectedEmds,
  onCancel,
  onClick,
}: IProps) => {
  const [position, setPosition] = useState<naver.maps.LatLng>();
  const {data, error, isFetching} = useQueryRegionByPoint(
    position && {lng: position.x, lat: position.y, type: GeoDataType.Emd},
    MAP_QUERY_DEBOUNCE_SHORT_MS
  );

  const [region, setRegion] = useState<string>();
  const [mapEvt, setEvents] = useState<any[]>([]);

  const [selectedEmd, setSelectedEmd] = useState<GeoDataItem>();
  const clickEventRef = useRef<any>(false);

  const isDuplicated =
    !!selectedEmds &&
    !!data &&
    selectedEmds.findIndex((i) => i.code === data.code) >= 0;

  const onMousemoveEvent = throttle((e: {coord: naver.maps.LatLng}) => {
    setPosition(e.coord);
  }, 500);

  const onMouseClick = (e: {originalEvent: MouseEvent}) => {
    if (!map || e?.originalEvent?.button !== 0) {
      return;
    }

    const prevCenter = map.getCenter();

    setTimeout(() => {
      const nextCenter = map.getCenter();

      if (prevCenter.toString() === nextCenter.toString() && data) {
        setSelectedEmd(data);
      }
    }, 100);
  };

  const onEscapePress = useCallback(() => {
    onCancel?.();
  }, [onCancel]);

  useEscpacePress(onEscapePress);

  useEffect(() => {
    if (selectedEmd) {
      onClick?.(selectedEmd);
      setSelectedEmd(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!selectedEmd]);

  useEffect(() => {
    if (data) {
      setRegion(data?.emd);
    } else {
      setRegion(undefined);
    }

    if (clickEventRef?.current) {
      naver.maps.Event.removeListener(clickEventRef?.current);
    }
    clickEventRef.current = naver.maps.Event.addListener(
      map,
      'mousedown',
      onMouseClick
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    if (error) {
      setRegion(undefined);
    }
  }, [error]);

  const regionText = isFetching ? '로딩 중...' : (region ?? '분석 불가 지역');

  useEffect(() => {
    mapEvt.push(
      naver.maps.Event.addListener(map, 'mousemove', onMousemoveEvent)
    );

    setEvents(mapEvt);

    return () => {
      if (clickEventRef?.current) {
        naver.maps.Event.removeListener(clickEventRef?.current);
      }
      mapEvt.forEach((evt) => {
        naver.maps.Event.removeListener(evt);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]);

  return (
    <>
      {!!position && (
        <DrawInfoCursor
          map={map}
          position={position}
          hint={isDuplicated ? '삭제' : '추가'}
          typeText={regionText}
          menu={{선택: '클릭', 취소: 'Esc'}}
          color={colorOptions.strokeColor}
        />
      )}
      {!!data && (
        <EmdArea
          map={map}
          item={data}
          highlightName={isDuplicated}
          displayNameTag={displayNameTag}
          colorOptions={colorOptions}
        />
      )}
    </>
  );
};
